/**
 * ArTableControls Component
 *
 * Renders table control buttons including add row and bulk actions.
 * Uses non-reactive store access to avoid unnecessary re-renders.
 *
 * Form data comes from `useFormByParentPath(parentPath)` — the table's own
 * parent, which is not necessarily the routed form. The only form-store leaves
 * it reads are `splitQuery` and `previewMode`, taken off one narrow snapshot:
 * `useFormStore()` is banned here because its spread of `formVersion.data`
 * re-rendered the whole control bar on every cell keystroke in the table
 * (SYD-2252).
 *
 * It holds no subscription to the version document at all — the sidebars resolve
 * their own source from `sourceParentPath`, behind `destroyOnHidden`. Every hook
 * mounted here answers to the same ban, `useSignifyConfigurationSidebar`
 * included (SYD-2264).
 *
 * The quote and order runs send a `rowScope` from `useArTableWorkflowRowScope`, so
 * they act on the rows the split, filters and view leave visible. It is handed to
 * `useWorkflowRunner` rather than awaited here, so the sidebar's own run state covers
 * the whole-table fetch it may perform; a refusal comes back as a `null` result and
 * abandons the run (SYD-2322).
 *
 * Both confirm handlers return their outcome to the sidebar and still close it on
 * success: each writes cells back into the form behind the drawer, and the order path
 * is irreversible, so the operator's next need is the updated form, not a banner
 * (SYD-2442).
 *
 * A Submit Order rejection carrying `data-loss` means Signify holds the order
 * while this form does not fully reflect it. That one gets a modal and closes the
 * sidebar: a 12-second toast beside a live Submit button invites exactly the
 * action that must not be taken blindly (SYD-2325). The code it branches on comes
 * off `workflowRunErrorOutcome`, which strips the `functions/` prefix the callable
 * client adds; compared raw, that branch never ran (SYD-2445).
 *
 * Two different elements scroll, so the strip is pinned twice over. Outside full
 * screen the document scrolls and the strip's container is as wide as the table,
 * so the strip spans it and its background covers every column beneath it; the
 * two button groups pin themselves `sticky` left and right to stay on screen. In
 * full screen the wrapper is the scrollport and the strip is only as wide as it,
 * so the strip pins at `top: 0` and its own `left` pin is what keeps it in view —
 * inert in the document-scroll path, where the strip already fills its container,
 * and load-bearing here. In card mode (below `md`) `ArTableBody` does not pin the
 * strip outside full screen, and the frozen-columns control is disabled: cards
 * have no columns to freeze.
 *
 * Do not cap the strip's width to the viewport to keep the buttons visible: that
 * leaves the strip narrower than the table and row content shows through the
 * uncovered band (SYD-2439).
 *
 * "+ Add Row" and the two single-part Signify inserts anchor on one resolver,
 * `resolveArTableInsertAfterRowId`, so an API-sourced part lands under the ticked
 * row instead of at the bottom of the table, and both get the same page demotion
 * (SYD-2373). They differ in one argument: with nothing ticked a typed row goes
 * under the page's last row and an API part appends at the table's end.
 *
 * The parts-list fetch keeps appending: it inserts many rows through one
 * callback, and a single anchor shared by all of them would land the list in
 * reverse.
 *
 * `FormUndoButtons` repeats the pair `FormControl` already carries, because in full
 * screen this strip is the only one on screen and a row delete has to stay visibly
 * reversible (SYD-2447). Full screen is also the whole of its condition: one table
 * holds the viewport at a time, so exactly one strip can carry the pair and its
 * accessible names stay unique however many tables a form renders. Outside full
 * screen the strip carries no pair and `FormControl`'s is the one in reach.
 */

import { FC, memo, useCallback, useMemo, useState, useContext } from 'react';
import {
  Button,
  Dropdown,
  Pagination,
  Popconfirm,
  theme,
  Typography,
} from 'antd';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import {
  ApiOutlined,
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  ClearOutlined,
  FilterOutlined,
  GroupOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import { ariaKeyshortcutsFor } from '@/keyboard';
import FlexBox from '@/components/atoms/FlexBox';
import Icon from '@/components/atoms/Icon';
import FormUndoButtons from '@/components/artifacts/form/FormUndoButtons';
import { ArTableActionsContext, ArTableUIStateContext } from '../context';
import { resolveManagedRows } from '../derives';
import { formDeriveCopy } from '@/services/formDerives';
import { ArTableViewSortContext } from '../context';
import ArBulkActionsMenu from './ArBulkActionsMenu';
import ArWorkflowMenu from './ArWorkflowMenu';
import { ArTableOptimizerControls } from '../optimizer';
import { useArSignifyInsert, QUANTITY_TYPE_ID } from '@/api/signify/arTable';
import { useSignifyProductModal } from '@/api/signify/SignifyProductButton/SignifyProductModal/use';
import SignifyProductModal from '@/api/signify/SignifyProductButton/SignifyProductModal';
import { useSignifyConfigurationSidebar } from '@/api/signify/SignifyProductButton/SignifyConfigurationSidebar/use';
import SignifyConfigurationSidebar from '@/api/signify/SignifyProductButton/SignifyConfigurationSidebar';
import { useSignifyFetchJobModal } from '@/api/signify/SignifyProductButton/SignifyFetchJobModal/use';
import SignifyFetchJobModal, {
  SignifyFetchJobMenuLabel,
} from '@/api/signify/SignifyProductButton/SignifyFetchJobModal';
import { useStoreDocument } from '@/store';
import type { MenuProps } from 'antd';
import { MessageContext } from '@/providers/MessageContext';
import { applyArTableSortAsOrder } from '@/services/arTableFunctions';
import type {
  ArCell,
  ArColumnDefinition,
  ArColumnReference,
  ArRowId,
  ArTableType,
} from '#types/System.Artifacts.types';
import type { ArTableId, TypeId } from '#types/System.Artifacts.types';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { UIElementType } from '#types/System.types';
import { useSnapshot } from 'valtio';
import { useArTableSplit } from '../split';
import { resolveArTableRowsOrderBy } from '../resolveArTableRowsOrderBy';
import { resolveArTableInsertAfterRowId } from '../resolveArTableInsertAfterRowId';
import { CAPABILITY_SIGNIFY_API } from '#types/Client.Parameters.types';
import { SIGNIFY_PRODUCTS_ENTITY_ID } from '#types/System.Parameters.types';
import type { FrozenLeftColumnsCount } from '../column/useFrozenLeftColumns';
import { useApplicableWorkflows } from '@/use/useApplicableWorkflows';
import WorkflowParameterSidebar from '@/workflows/runtime/WorkflowParameterSidebar';
import type { SplitQuery, UIArTableColumnOverride } from '#types/System.types';
import type { Workflow, WorkflowRunAttachments } from '#types';
import type { WorkflowRunOutcome } from '@/workflows/runtime/workflowRunOutcome.types';
import { formStore, PageScopeContext } from '@/store';
import {
  arTableFullScreenActions,
  arTableFullScreenState,
  PAGE_CHROME_STICKY_TOP,
} from '@/store/arTableFullScreenStore';
import { canFullScreenTable } from '../canFullScreenTable';
import resolveArColumnLabel from '../column/resolveArColumnLabel';
import { useFormByParentPath } from '@/workflows/runtime/useFormByParentPath';
import {
  clearSelection,
  getSelectedRowIds,
  type ArTableSelectionState,
} from '@/workflows/runtime/arTableSelection';
import {
  selectCreateQuoteWorkflow,
  selectCreateOrderWorkflow,
} from '@/workflows/runtime/workflowPredicates';
import { useWorkflowRunner } from '@/workflows/runtime/useWorkflowRunner';
import { useArTableWorkflowRowScope } from '../visibleRows';
import { buildSignifyApiMenuItems } from '@/workflows/runtime/signifyApiMenuItems';
import { workflowRunErrorOutcome } from '@/workflows/runtime/workflowRunErrorOutcome';
import { workflowRunStoppedOutcome } from '@/workflows/runtime/workflowRunStoppedOutcome';
import formatSignifySuccessMessage from './formatSignifySuccessMessage';

type ArTableControlsProps = {
  tableId: ArTableId;
  parentPath: string;
  disabled?: boolean;
  stickyTop?: boolean;
  frozenLeftColumns: FrozenLeftColumnsCount;
  onFrozenLeftColumnsChange: (count: FrozenLeftColumnsCount) => void;
  columnConfig?: UIArTableColumnOverride[];
  footerOnly?: boolean;
  cardMode?: boolean;
};

/**
 * ArTableControls renders control buttons for table operations.
 */
const ArTableControls: FC<ArTableControlsProps> = ({
  tableId,
  parentPath,
  disabled = false,
  stickyTop = false,
  frozenLeftColumns,
  onFrozenLeftColumnsChange,
  columnConfig,
  footerOnly = false,
  cardMode = false,
}) => {
  const { isPdfExportMode } = usePdfExportContext();
  const { token } = theme.useToken();
  const stickyMinHeight =
    (token.controlHeightSM ?? token.controlHeight) + token.paddingSM * 2;
  const [adding, setAdding] = useState(false);
  const [sortingAsOrder, setSortingAsOrder] = useState(false);
  const { messageApi, modal, handleFirestoreWriteError } =
    useContext(MessageContext);
  const { fieldTypes } = useContext(FieldTypeContext);
  const actions = useContext(ArTableActionsContext)!;
  const viewSortCtx = useContext(ArTableViewSortContext)!;
  const uiState = useContext(ArTableUIStateContext)!;
  const tableSelection = uiState.selection as ArTableSelectionState;
  const filteringSnap = useSnapshot(uiState.filtering);
  const paginationSnap = useSnapshot(uiState.pagination);
  /**
   * The managed-row split. Both numbers come from the rows on screen, never from
   * `totalRows`: only the current page is loaded, so a table-wide managed count
   * cannot be told from here and a wrong one is worse than a page-wide one.
   */
  const renderedRowIdsSnap = useSnapshot(uiState.renderedRowIds);
  const deriveProjectionsSnap = useSnapshot(uiState.deriveProjections);
  const { managedRowCount } = resolveManagedRows(
    deriveProjectionsSnap,
    renderedRowIdsSnap,
  );
  const viewSort = viewSortCtx.viewSort;
  const rowsOrderBy = useMemo(() => {
    return resolveArTableRowsOrderBy(viewSort);
  }, [viewSort]);
  const { state: schemaState } = useStoreDocument<ArTableType>('schema');
  const schemaDataSnap = useSnapshot(schemaState.data!);
  const columns = schemaDataSnap.columns as ArColumnDefinition[];
  const optimizerConfig = schemaDataSnap.optimizer;
  const controlsConfig = schemaDataSnap.controls;
  const protectedConfig = schemaDataSnap.protected;

  const groupsEnabled = schemaDataSnap.bundles?.groups === true;
  const kitsEnabled = schemaDataSnap.bundles?.kits?.enabled === true;
  const selectionSnap = useSnapshot(uiState.selection);
  const protectedTypeIds = new Set<TypeId>(
    protectedConfig?.protectedTypeIds ?? [],
  );

  const showSignifyAPIControls =
    controlsConfig?.showSignifyAPIControls !== false;

  const lineItemsColumn = useMemo(() => {
    return columns.find(
      (
        col,
      ): col is ArColumnReference & {
        referenceType: 'entity';
        entityId: string;
      } => {
        return (
          col.uiElementType === UIElementType.Reference &&
          col.referenceType === 'entity' &&
          col.entityId === SIGNIFY_PRODUCTS_ENTITY_ID
        );
      },
    );
  }, [columns]);

  const formSnap = useSnapshot(formStore.state);
  const splitQuery = formSnap.splitQuery as SplitQuery | undefined;
  /**
   * A workflow run is building its per-recipient documents off the live filters
   * and the live page, so the two filter resets and the pager are disabled for
   * its duration — clearing a filter or turning a page mid-run widens every
   * document built after it, and a page change clears the selection outright
   * (SYD-2410). This whole bar is absent in PDF export mode, so the portal's own
   * copies never see the lock.
   */
  const rowScopeLocked = formSnap.isDispatchingWorkflowRun;
  const pageScope = useSnapshot(useContext(PageScopeContext));
  const isFullScreen =
    useSnapshot(arTableFullScreenState).tableKey ===
    formStore.tableKey(parentPath, tableId);
  const fullScreenEligible = canFullScreenTable({
    pageScopeSource: pageScope.source,
    formPreviewMode: formSnap.previewMode,
  });
  const { formId, versionId, formType, formNumber } =
    useFormByParentPath(parentPath);
  const split = useArTableSplit(columns);

  const { insertRowFromLineItem, insertManualRow, findRowsHoldingLineItems } =
    useArSignifyInsert({
      columns,
      lineItemsColumn,
      protectedTypeIds,
      disabled,
    });

  /**
   * The whole anchor — ticked rows, page demotion and all — read off the bare
   * proxies at the moment the handler runs. For the Signify paths that moment is
   * after the modal's own awaited record write, not before it: `onSuccess` fires
   * on the line above `closeModal`, so the modal is still over the table and no
   * tick can have landed in between (SYD-2373).
   */
  const resolveInsertAfterRowId = useCallback(
    (
      whenNothingTicked: 'lastRenderedRow' | 'tableEnd',
    ): ArRowId | undefined => {
      return resolveArTableInsertAfterRowId({
        selection: tableSelection,
        renderedRowIds: uiState.renderedRowIds,
        pagination: uiState.pagination,
        whenNothingTicked,
      });
    },
    [tableSelection, uiState],
  );

  const handleSignifyInsertSuccess = useCallback(
    (entityRecordId: string) => {
      const afterRowId = resolveInsertAfterRowId('tableEnd');
      void insertRowFromLineItem(
        entityRecordId,
        afterRowId ? { afterRowId } : undefined,
      );
    },
    [insertRowFromLineItem, resolveInsertAfterRowId],
  );

  const handleSignifyManualInsert: typeof insertManualRow = useCallback(
    (recordName, fields, entityReferences, options) => {
      const afterRowId = resolveInsertAfterRowId('tableEnd');
      return insertManualRow(
        recordName,
        fields,
        entityReferences,
        afterRowId ? { ...options, afterRowId } : options,
      );
    },
    [insertManualRow, resolveInsertAfterRowId],
  );

  const {
    openModal: openSignifyModal,
    configDisabledReason: signifyConfigDisabledReason,
    ...signifyModalProps
  } = useSignifyProductModal({
    onSuccess: handleSignifyInsertSuccess,
    onManualInsert: handleSignifyManualInsert,
  });
  /**
   * The multi-part fetch writes its quantities into the row's `quantity` cell, so a table without
   * that column — or one that protects it — has nowhere to put them, and the modal disables its Qty
   * inputs with that reason rather than dropping the column.
   */
  const hasQuantityColumn = columns.some(
    (c) => c.typeId === QUANTITY_TYPE_ID && !protectedTypeIds.has(c.typeId),
  );
  const signifyFetchJobTargetKey = formStore.tableKey(parentPath, tableId);
  /**
   * The same condition the modal and the API menu are gated on. The hook cannot be called
   * conditionally, so the gate travels into it: its reattach read is a `getDocs` over the
   * operator's fetch jobs, and a table that can never show the modal must not pay for one.
   */
  const signifyFetchJobEnabled =
    CAPABILITY_SIGNIFY_API && showSignifyAPIControls && !!lineItemsColumn;
  const { openModal: openSignifyFetchJobModal, ...signifyFetchJobModalProps } =
    useSignifyFetchJobModal({
      targetKey: signifyFetchJobTargetKey,
      hasQuantityColumn,
      disabled,
      enabled: signifyFetchJobEnabled,
      onSuccess: insertRowFromLineItem,
      onManualInsert: insertManualRow,
      onFindInsertedRows: findRowsHoldingLineItems,
    });
  const {
    openSidebar: openSignifyConfigurationSidebar,
    hasActiveSession,
    ...signifyConfigurationProps
  } = useSignifyConfigurationSidebar({
    tableId,
    onSuccess: handleSignifyInsertSuccess,
    onManualInsert: handleSignifyManualInsert,
    disabled,
  });

  const rfqWorkflowQueryEnabled = !!formId && !!versionId;
  const { workflows: applicableWorkflows } = useApplicableWorkflows({
    triggerType: 'bulkRowAction',
    formType,
    tableId,
    enabled: rfqWorkflowQueryEnabled,
  });
  const rfqWorkflow = useMemo(() => {
    return selectCreateQuoteWorkflow(applicableWorkflows);
  }, [applicableWorkflows]);
  const orderWorkflow = useMemo(() => {
    return selectCreateOrderWorkflow(applicableWorkflows);
  }, [applicableWorkflows]);
  const [rfqSidebarWorkflow, setRfqSidebarWorkflow] = useState<Workflow | null>(
    null,
  );
  const [orderSidebarWorkflow, setOrderSidebarWorkflow] =
    useState<Workflow | null>(null);
  const { resolveRowScope } = useArTableWorkflowRowScope({
    tableId,
    parentPath,
  });
  const {
    isRunning: rfqExecuting,
    runWorkflow: runRfqWorkflow,
    stopRun: stopRfqRun,
    stopPhase: rfqStopPhase,
  } = useWorkflowRunner({
    sourceFormId: formId,
    sourceVersionId: versionId,
    sourceTableId: tableId,
    resolveRowScope,
  });
  const {
    isRunning: orderExecuting,
    runWorkflow: runOrderWorkflow,
    stopRun: stopOrderRun,
    stopPhase: orderStopPhase,
  } = useWorkflowRunner({
    sourceFormId: formId,
    sourceVersionId: versionId,
    sourceTableId: tableId,
    resolveRowScope,
  });

  const handleRfqConfirm = useCallback(
    async (
      params: Record<string, unknown>,
      attachments?: WorkflowRunAttachments,
    ): Promise<WorkflowRunOutcome | null> => {
      const w = rfqSidebarWorkflow;
      if (!w?.docId || !formId || !versionId) return null;
      try {
        const result = await runRfqWorkflow({
          workflow: w,
          userParameters: params,
          selectedRowIds: getSelectedRowIds(tableSelection),
          attachments,
        });
        if (!result) return null;
        if (!result.success) {
          const stopped = workflowRunStoppedOutcome(result, w);
          messageApi.warning(stopped.text, 10);
          return stopped;
        }

        const successMessage = `${w.description?.shortLabel ?? 'Create/Revise Quote'} completed.`;
        messageApi.success(successMessage);
        setRfqSidebarWorkflow(null);
        clearSelection(tableSelection);
        return {
          status: 'success',
          text: successMessage,
          summary: result.summaries?.find((s) => s.actionType === 'send'),
        };
      } catch (error) {
        const outcome = workflowRunErrorOutcome(
          error,
          w.description?.shortLabel ?? 'Create/Revise Quote',
        );
        handleFirestoreWriteError(
          error,
          '[ArTableControls] Create/Revise Quote',
          { userMessage: outcome.text },
        );
        return outcome;
      }
    },
    [
      formId,
      versionId,
      messageApi,
      rfqSidebarWorkflow,
      runRfqWorkflow,
      tableSelection,
      handleFirestoreWriteError,
    ],
  );

  const handleOrderConfirm = useCallback(
    async (
      params: Record<string, unknown>,
      attachments?: WorkflowRunAttachments,
    ): Promise<WorkflowRunOutcome | null> => {
      const w = orderSidebarWorkflow;
      if (!w?.docId || !formId || !versionId) return null;
      try {
        const result = await runOrderWorkflow({
          workflow: w,
          userParameters: params,
          selectedRowIds: getSelectedRowIds(tableSelection),
          attachments,
        });
        if (!result) return null;
        if (!result.success) {
          const stopped = workflowRunStoppedOutcome(result, w);
          messageApi.warning(stopped.text, 10);
          return stopped;
        }

        const apiResult = result.actions?.findLast(
          (a) => a.actionType === 'callSignifyApi',
        )?.result as Record<string, unknown> | undefined;
        const successMessage = formatSignifySuccessMessage(
          apiResult,
          w.description?.shortLabel ?? 'Submit Order',
        );
        messageApi.success(successMessage, 10);
        if (
          typeof apiResult?.warning === 'string' &&
          apiResult.warning.length > 0
        ) {
          messageApi.warning(apiResult.warning, 15);
        }
        setOrderSidebarWorkflow(null);
        clearSelection(tableSelection);
        return {
          status: 'success',
          text: successMessage,
          summary: result.summaries?.find((s) => s.actionType === 'send'),
        };
      } catch (error) {
        const outcome = workflowRunErrorOutcome(
          error,
          w.description?.shortLabel ?? 'Submit Order',
        );

        if (outcome.code === 'data-loss') {
          setOrderSidebarWorkflow(null);
          console.error('[ArTableControls] Submit Order', error);
          modal.error({
            title: 'The order is placed — this form is not fully updated',
            content: outcome.text,
            okText: 'Understood',
          });
          return outcome;
        }

        handleFirestoreWriteError(error, '[ArTableControls] Submit Order', {
          userMessage: outcome.text,
        });
        return outcome;
      }
    },
    [
      formId,
      versionId,
      messageApi,
      modal,
      orderSidebarWorkflow,
      runOrderWorkflow,
      tableSelection,
      handleFirestoreWriteError,
    ],
  );

  const signifyApiMenuItems: NonNullable<MenuProps['items']> = useMemo(() => {
    return buildSignifyApiMenuItems({
      disabled,
      configDisabledReason: signifyConfigDisabledReason,
      hasActiveSession,
      rfqWorkflow,
      isRfqRunning: rfqExecuting,
      orderWorkflow,
      isOrderRunning: orderExecuting,
      bomIsEmpty: paginationSnap.totalRows === 0,
      partsListLabel: (
        <SignifyFetchJobMenuLabel
          targetKey={signifyFetchJobTargetKey}
          reason={signifyConfigDisabledReason}
        />
      ),
      onAddFromSignify: openSignifyModal,
      onAddPartsListFromSignify: openSignifyFetchJobModal,
      onConfigureSignify: openSignifyConfigurationSidebar,
      onRunRfq: () => {
        if (!rfqWorkflow) return;
        setRfqSidebarWorkflow(rfqWorkflow);
      },
      onRunOrder: () => {
        if (!orderWorkflow) return;
        setOrderSidebarWorkflow(orderWorkflow);
      },
    });
  }, [
    disabled,
    signifyConfigDisabledReason,
    signifyFetchJobTargetKey,
    hasActiveSession,
    openSignifyConfigurationSidebar,
    openSignifyFetchJobModal,
    openSignifyModal,
    rfqWorkflow,
    rfqExecuting,
    orderWorkflow,
    orderExecuting,
    paginationSnap.totalRows,
  ]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page > 1 && !uiState.paginationCursors.byPage.has(page - 1)) return;
      uiState.pagination.currentPage = page;
      clearSelection(tableSelection);
    },
    [uiState, tableSelection],
  );

  const hasActiveFilters = Object.values(filteringSnap.byColumnId).some(
    (v) => v && v.length > 0,
  );
  const activeFilterCount = Object.values(filteringSnap.byColumnId).filter(
    (v) => v && v.length > 0,
  ).length;

  const handleCreateBundle = (type: 'group' | 'kit') => {
    const renderedRowIds = uiState.renderedRowIds;
    const selectedRows = uiState.selection.selectedRows;
    const selectedInOrder = renderedRowIds.filter(
      (id) => selectedRows[id] === true,
    ) as ArRowId[];
    actions.createBundle(selectedInOrder, type);
    if (selectedInOrder.length > 0) clearSelection(tableSelection);
  };

  const handleAddRow = async () => {
    setAdding(true);
    try {
      const initialCells = split
        ? ({ [split.typeId]: { value: split.filterValue } } satisfies Record<
            TypeId,
            ArCell
          >)
        : undefined;

      /** Nothing ticked: a typed row goes under the last row on the page, where the operator is looking. */
      const afterRowId = resolveInsertAfterRowId('lastRenderedRow');

      await actions.addRow(
        initialCells,
        afterRowId ? { afterRowId } : undefined,
      );
    } finally {
      setAdding(false);
    }
  };

  const setViewSort = viewSortCtx.setViewSort;

  const columnLabelByTypeId = new Map<TypeId, string>(
    columns.map((c) => [c.typeId, resolveArColumnLabel(c, fieldTypes)]),
  );

  const currentSortableColumn =
    viewSort.mode === 'column'
      ? columns.find(
          (c) => c.typeId === viewSort.typeId && c.sortableAsOrder === true,
        )
      : undefined;

  const bakeSortToManual = async (params: {
    typeId: TypeId;
    direction: 'asc' | 'desc';
  }): Promise<boolean> => {
    if (disabled) return false;
    setSortingAsOrder(true);
    try {
      await applyArTableSortAsOrder({
        parentPath,
        tableId,
        typeId: params.typeId,
        direction: params.direction,
      });
      messageApi.success('Sort applied to manual order.');
      return true;
    } catch (error) {
      handleFirestoreWriteError(error, '[ArTableControls] bake sort as order', {
        userMessage: 'Failed to apply sort. Please try again.',
      });
      return false;
    } finally {
      setSortingAsOrder(false);
    }
  };

  const activeColumnSortItems: NonNullable<MenuProps['items']> =
    viewSort.mode === 'column'
      ? [
          {
            key: 'sort-asc',
            label: 'Sort ascending',
            disabled,
            onClick: () =>
              setViewSort({
                mode: 'column',
                typeId: viewSort.typeId,
                direction: 'asc',
              }),
          },
          {
            key: 'sort-desc',
            label: 'Sort descending',
            disabled,
            onClick: () =>
              setViewSort({
                mode: 'column',
                typeId: viewSort.typeId,
                direction: 'desc',
              }),
          },
          {
            key: 'sort-clear',
            label: 'Clear sort (back to Manual)',
            disabled,
            onClick: () => setViewSort({ mode: 'order' }),
          },
        ]
      : [];

  const sortableColumns = columns.filter((c) => c.sortableAsOrder === true);

  const manualSortByGroup: NonNullable<MenuProps['items']> =
    viewSort.mode === 'order'
      ? [
          {
            key: 'sort-by-group',
            type: 'group',
            label: 'Sort by',
            children: sortableColumns.map((col) => ({
              key: `sort-by-${col.typeId}`,
              label: resolveArColumnLabel(col, fieldTypes),
              disabled,
              onClick: () =>
                setViewSort({
                  mode: 'column',
                  typeId: col.typeId,
                  direction: 'asc',
                }),
            })),
          },
        ]
      : [];

  const sortMenuItems: NonNullable<MenuProps['items']> = [
    ...activeColumnSortItems,
    ...manualSortByGroup,
    ...(currentSortableColumn
      ? ([
          { type: 'divider' as const },
          {
            key: 'bake-sort-as-order',
            label: (
              <Popconfirm
                title="Apply sort to Manual order?"
                description="This will permanently rewrite the row order for this table."
                okText="Apply"
                cancelText="Cancel"
                onConfirm={async () => {
                  if (viewSort.mode !== 'column') return;
                  if (!currentSortableColumn) return;
                  const { typeId, direction } = viewSort;
                  const ok = await bakeSortToManual({ typeId, direction });
                  if (ok) {
                    setViewSort({ mode: 'order' });
                  }
                }}
                disabled={
                  disabled || sortingAsOrder || viewSort.mode !== 'column'
                }
              >
                <span onClick={(e) => e.stopPropagation()}>
                  Bake current sort into Manual order…
                </span>
              </Popconfirm>
            ),
            disabled: disabled || sortingAsOrder || viewSort.mode !== 'column',
          },
        ] as NonNullable<MenuProps['items']>)
      : []),
  ];

  const sortPillLabel =
    viewSort.mode === 'order'
      ? 'Manual Sort'
      : viewSort.mode === 'system'
        ? `Sort: ${
            viewSort.key === 'createdAt'
              ? 'Created'
              : viewSort.key === 'modifiedAt'
                ? 'Modified'
                : 'Locked'
          }`
        : `Sort: ${columnLabelByTypeId.get(viewSort.typeId) ?? viewSort.typeId}`;

  const handleClearAllFilters = () => {
    uiState.filtering.byColumnId = {};
  };

  const handleClearAll = () => {
    uiState.filtering.byColumnId = {};
    if (viewSort.mode !== 'order') setViewSort({ mode: 'order' });
  };

  const hasAnyViewSettings = hasActiveFilters || viewSort.mode !== 'order';

  if (isPdfExportMode) {
    return null;
  }

  const frozenColumnsMenuItems: NonNullable<MenuProps['items']> = [
    {
      key: 'frozen-cols-group',
      type: 'group',
      label: 'Left frozen columns',
      children: ([0, 1, 2, 3] as const).map((count) => ({
        key: `frozen-cols-${count}`,
        label:
          count === 0 ? 'None' : `${count} column${count === 1 ? '' : 's'}`,
        disabled,
        onClick: () => onFrozenLeftColumnsChange(count),
      })),
    },
  ];

  /**
   * One scope object for both Signify sidebars: the RFQ and the Order run over the
   * same selection and the same active split, and two literals drifting apart is
   * the defect this replaces. `selectedCount` comes off `selectionSnap` because it
   * renders — the bare proxy read does not subscribe.
   */
  const tableScope = {
    selectedCount: selectionSnap.selectedCount,
    totalCount: paginationSnap.totalRows,
    selectedRowIds: getSelectedRowIds(tableSelection),
    splitLabel: splitQuery?.queryValue,
    splitFilter: splitQuery
      ? {
          columnId: splitQuery.splitRef.valuePath,
          value: splitQuery.queryValue,
        }
      : undefined,
  };

  return (
    <FlexBox
      aria-label="artable-controls"
      style={{
        padding: token.paddingSM,
        backgroundColor: token.colorFillQuaternary,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        boxSizing: 'border-box',
        ...(stickyTop
          ? {
              position: 'sticky',
              left: 0,
              top: isFullScreen ? 0 : PAGE_CHROME_STICKY_TOP,
              zIndex: token.zIndexPopupBase - 2,
              minHeight: stickyMinHeight,
            }
          : null),
      }}
      wrap
    >
      {!footerOnly && (
        <FlexBox wrap noGrow style={{ position: 'sticky', left: 0 }}>
          <Button
            aria-label="artable-add-row-button"
            type="text"
            icon={<PlusOutlined />}
            onClick={handleAddRow}
            loading={adding}
            disabled={disabled}
            size="small"
          >
            Add Row
          </Button>

          {groupsEnabled && kitsEnabled && (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'create-group',
                    label:
                      selectionSnap.selectedCount > 0
                        ? 'Create Group from Selection'
                        : 'Create Group',
                    icon: <GroupOutlined />,
                    onClick: () => handleCreateBundle('group'),
                    disabled,
                  },
                  {
                    key: 'create-kit',
                    label:
                      selectionSnap.selectedCount > 0
                        ? 'Create Kit from Selection'
                        : 'Create Kit',
                    icon: <AppstoreOutlined />,
                    onClick: () => handleCreateBundle('kit'),
                    disabled,
                  },
                ],
              }}
              trigger={['click']}
              disabled={disabled}
            >
              <Button
                aria-label="artable-bundle-menu"
                type="text"
                icon={<GroupOutlined />}
                size="small"
                disabled={disabled}
              >
                Bundle
              </Button>
            </Dropdown>
          )}

          {groupsEnabled && !kitsEnabled && (
            <Button
              aria-label="artable-create-group-button"
              type="text"
              icon={<GroupOutlined />}
              onClick={() => handleCreateBundle('group')}
              disabled={disabled}
              size="small"
            >
              {selectionSnap.selectedCount > 0
                ? 'Create Group from Selection'
                : 'Create Group'}
            </Button>
          )}

          {kitsEnabled && !groupsEnabled && (
            <Button
              aria-label="artable-create-kit-button"
              type="text"
              icon={<AppstoreOutlined />}
              onClick={() => handleCreateBundle('kit')}
              disabled={disabled}
              size="small"
            >
              {selectionSnap.selectedCount > 0
                ? 'Create Kit from Selection'
                : 'Create Kit'}
            </Button>
          )}

          {CAPABILITY_SIGNIFY_API &&
            showSignifyAPIControls &&
            lineItemsColumn && (
              <Dropdown
                menu={{ items: signifyApiMenuItems }}
                trigger={['click']}
                disabled={disabled}
                styles={{ root: { width: 300 } }}
              >
                <Button
                  aria-label="artable-signify-api-menu"
                  type="text"
                  size="small"
                  icon={<ApiOutlined />}
                  disabled={disabled}
                >
                  API
                </Button>
              </Dropdown>
            )}

          <Dropdown
            menu={{ items: frozenColumnsMenuItems }}
            trigger={['click']}
            disabled={disabled || cardMode}
          >
            <Button
              type="text"
              aria-label="artable-frozen-columns-control"
              size="small"
              icon={<PushpinOutlined />}
              disabled={disabled || cardMode}
            >
              {frozenLeftColumns}
            </Button>
          </Dropdown>

          <Dropdown
            menu={{ items: sortMenuItems }}
            trigger={['click']}
            disabled={disabled}
          >
            <Button
              type="text"
              aria-label="artable-sort-control"
              size="small"
              icon={
                viewSort.mode === 'column' ? (
                  viewSort.direction === 'asc' ? (
                    <ArrowUpOutlined />
                  ) : (
                    <ArrowDownOutlined />
                  )
                ) : (
                  <UnorderedListOutlined />
                )
              }
            >
              {sortPillLabel}
            </Button>
          </Dropdown>

          {hasAnyViewSettings && (
            <Button
              type="text"
              aria-label="artable-clear-view"
              size="small"
              icon={<ClearOutlined />}
              onClick={handleClearAll}
              disabled={rowScopeLocked}
              danger
            >
              Clear View
            </Button>
          )}

          {hasActiveFilters && (
            <Button
              type="text"
              aria-label="artable-clear-filters"
              size="small"
              icon={<FilterOutlined />}
              onClick={handleClearAllFilters}
              disabled={rowScopeLocked}
            >
              {activeFilterCount} Filter{activeFilterCount > 1 ? 's' : ''}
            </Button>
          )}

          <ArBulkActionsMenu disabled={disabled} />
        </FlexBox>
      )}
      <FlexBox noGrow wrap style={{ position: 'sticky', right: 0 }}>
        {!footerOnly && optimizerConfig?.enabled === true && (
          <ArTableOptimizerControls
            tableId={tableId}
            parentPath={parentPath}
            rowsOrderBy={rowsOrderBy}
            isPaginated={paginationSnap.totalPages > 1}
            disabled={disabled}
          />
        )}
        <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
          {paginationSnap.totalRows} rows
        </Typography.Text>
        {managedRowCount > 0 && (
          <Typography.Text
            type="secondary"
            aria-label="artable-managed-rows"
            style={{ whiteSpace: 'nowrap' }}
          >
            {paginationSnap.totalPages > 1
              ? formDeriveCopy.managedRowSplitOnPage(
                  managedRowCount,
                  renderedRowIdsSnap.length,
                )
              : formDeriveCopy.managedRowSplit(
                  managedRowCount,
                  renderedRowIdsSnap.length,
                )}
          </Typography.Text>
        )}
        {paginationSnap.totalPages > 1 && (
          <Pagination
            current={paginationSnap.currentPage}
            total={paginationSnap.totalRows}
            pageSize={paginationSnap.pageSize}
            onChange={handlePageChange}
            disabled={rowScopeLocked}
            size="small"
            showSizeChanger={false}
            simple={{ readOnly: true }}
          />
        )}
        {!footerOnly && isFullScreen && (
          <FormUndoButtons surface="table controls" />
        )}
        {!footerOnly && (
          <ArWorkflowMenu
            tableId={tableId}
            parentPath={parentPath}
            disabled={disabled}
            columnConfig={columnConfig}
          />
        )}
        {!footerOnly && fullScreenEligible && (
          <Button
            type="text"
            size="small"
            aria-label="artable-full-screen-toggle"
            aria-keyshortcuts={ariaKeyshortcutsFor('table.toggleFullScreen')}
            icon={isFullScreen ? <Icon.Minimize /> : <Icon.Maximize />}
            onClick={() =>
              arTableFullScreenActions.toggle(
                formStore.tableKey(parentPath, tableId),
              )
            }
          />
        )}
      </FlexBox>
      {CAPABILITY_SIGNIFY_API && showSignifyAPIControls && lineItemsColumn && (
        <>
          <SignifyProductModal {...signifyModalProps} />
          <SignifyFetchJobModal {...signifyFetchJobModalProps} />
          <SignifyConfigurationSidebar
            {...signifyConfigurationProps}
            hasActiveSession={hasActiveSession}
          />
        </>
      )}
      <WorkflowParameterSidebar
        workflow={rfqSidebarWorkflow}
        open={rfqSidebarWorkflow !== null}
        sourceParentPath={parentPath}
        sourceFormId={formId}
        sourceVersionId={versionId}
        tableScope={tableScope}
        onCancel={() => setRfqSidebarWorkflow(null)}
        onConfirm={handleRfqConfirm}
        onStop={stopRfqRun}
        stopPhase={rfqStopPhase}
        formNumber={formNumber}
        formTitle={formNumber ?? 'Form'}
      />
      <WorkflowParameterSidebar
        workflow={orderSidebarWorkflow}
        open={orderSidebarWorkflow !== null}
        sourceParentPath={parentPath}
        sourceFormId={formId}
        sourceVersionId={versionId}
        tableScope={tableScope}
        onCancel={() => setOrderSidebarWorkflow(null)}
        onConfirm={handleOrderConfirm}
        onStop={stopOrderRun}
        stopPhase={orderStopPhase}
        formNumber={formNumber}
        formTitle={formNumber ?? 'Form'}
      />
    </FlexBox>
  );
};

export default memo(ArTableControls);
